import type { LatLngBounds, Map, MarkerWithCircle } from "../types";
import { MAX_ZOOM_LEVEL } from "../const";
import { moveMapToPosition } from "./moveMapToPosition";
import { latRad } from "./index";
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

export const getMarkersBounds = (markers: MarkerWithCircle[]): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds();

  markers.forEach(({ marker }) => {
    const pos = (marker as AdvancedMarkerElement).position;
    if (pos) {
      bounds.extend(pos);
    }
  });

  return bounds;
};

export const moveMapToBounds = (map: Map, bounds: LatLngBounds, mapElement: HTMLElement, padding: number = 0, duration?: number): void => {
  const mapZoomLevel = map.getZoom();

  if (typeof mapZoomLevel === "undefined") {
    return;
  }

  const requiredZoomLevel = getBoundsZoomLevel(bounds, mapElement.getBoundingClientRect());
  const zoomDiff = requiredZoomLevel - map.getZoom()!;
  const zoom = requiredZoomLevel - padding;
  const center = bounds.getCenter();
  const position = { lat: center.lat(), lng: center.lng() };
  if (zoomDiff > padding) {
    moveMapToPosition(map, position, zoom, duration);
  } else if (mapZoomLevel < requiredZoomLevel) {
    moveMapToPosition(map, position, requiredZoomLevel, duration);
  } else {
    moveMapToPosition(map, position, MAX_ZOOM_LEVEL, duration);
  }
};

export const getBoundsZoomLevel = (bounds: LatLngBounds, mapDim: { height: number; width: number }) => {
  const WORLD_DIM = { height: 256, width: 256 };

  const zoom = (mapPx: number, worldPx: number, fraction: number): number => Math.log(mapPx / worldPx / fraction) / Math.LN2;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, MAX_ZOOM_LEVEL);
};
